import { useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { URL } from "../../store/utils";
import { handleSocketSessionExpiry } from "../../utils/dashboardUtils";
import useLatestToken, { attachTokenOnReconnect } from "../useLatestToken";
import logger from "../../utils/logger";

/**
 * Onay Bekleyen Cihazlar canli guncellemeleri icin Socket.IO hook'u.
 * Server'dan gelen `pendingDeviceUpdate` ve `pendingDeviceRemove` olaylarini
 * dinler; kayit gelir/guncellenirse listeye ekler, onaylanip silindiyse listeden cikarir.
 */
export const usePendingDevicesSocket = (token) => {
  const { hasToken, tokenRef } = useLatestToken(token);
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketItems, setSocketItems] = useState([]);
  const [removedIds, setRemovedIds] = useState(new Set());
  const [newItemIds, setNewItemIds] = useState(new Set());

  const mergePending = useCallback((pending) => {
    setSocketItems((prev) => {
      const existingIndex = prev.findIndex((p) => p._id === pending._id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = pending;
        return updated;
      }
      return [pending, ...prev];
    });

    setNewItemIds((prev) => new Set(prev).add(pending._id));
    setTimeout(() => {
      setNewItemIds((prev) => {
        const next = new Set(prev);
        next.delete(pending._id);
        return next;
      });
    }, 600);
  }, []);

  const removePending = useCallback((id) => {
    if (!id) return;
    setSocketItems((prev) => prev.filter((p) => p._id !== id));
    setRemovedIds((prev) => new Set(prev).add(id));
  }, []);

  useEffect(() => {
    if (!hasToken) return;

    if (
      socketRef.current &&
      (socketRef.current.connected || socketRef.current.connecting)
    ) {
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socketUrl = URL.url;

    socketRef.current = io(socketUrl, {
      transports: ["websocket", "polling"],
      timeout: 5000,
      query: { token: tokenRef.current },
    });

    attachTokenOnReconnect(socketRef.current, tokenRef); // #142

    socketRef.current.on("connect", () => {
      setSocketConnected(true);
    });

    socketRef.current.on("connect_error", (error) => {
      logger.error("Socket.IO baglanti hatasi:", error.message);
    });

    // Server iki farkli isim conventionu yayinliyor — ikisini de dinle
    const updateHandler = (pending) => {
      if (pending && pending._id) mergePending(pending);
    };
    const removeHandler = (payload) => {
      const id = typeof payload === "string" ? payload : payload?._id;
      removePending(id);
    };
    socketRef.current.on("pendingDeviceUpdate", updateHandler);
    socketRef.current.on("pendingDeviceRemove", removeHandler);
    // Yeni REGISTER (pendingDeviceRegister.mqtt.js) ve admin
    // claim/assign (pendingDevice.service.js) bu eventleri yayiniyor
    socketRef.current.on("pendingDevice:created", updateHandler);
    socketRef.current.on("pendingDevice:updated", updateHandler);
    socketRef.current.on("pendingDevice:claimed", updateHandler);
    // assigned: kayit "assigned" status'a gecti — listede goster, status
    // farkli olarak. Eger admin filter "pending" ise client filtresinde
    // dusecek.
    socketRef.current.on("pendingDevice:assigned", updateHandler);

    socketRef.current.on("error", (payload) => {
      const reason = payload?.reason;
      setSocketConnected(false);
      if (reason && handleSocketSessionExpiry(reason)) return;
    });

    socketRef.current.on("disconnect", () => {
      setSocketConnected(false);
    });

    return () => {
      if (socketRef.current) {
        setSocketConnected(false);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [hasToken, tokenRef, mergePending, removePending]);

  return { socketConnected, socketItems, removedIds, newItemIds };
};
